import { Link } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const values = [
  {
    icon: "ri-video-line",
    title: "内容为先",
    description: "坚持以高清、多元的视频内容为核心，让每一次观看都值得。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "分类清晰",
    description: "细致的分类与标签体系，帮助你在最短时间找到想看的内容。",
  },
  {
    icon: "ri-shield-check-line",
    title: "体验友好",
    description: "简洁的界面、流畅的交互，让浏览视频成为一种轻松的享受。",
  },
  {
    icon: "ri-heart-3-line",
    title: "分享共创",
    description: "鼓励每一位用户收藏与分享，让好内容被更多人看见。",
  },
];

const milestones = [
  { year: "2021", title: "平台起步", text: "啪啪视频正式上线，以娱乐与生活类内容开启第一站。" },
  { year: "2023", title: "分类完善", text: "新增教育、科技、音乐等分类，视频资源突破一万条。" },
  { year: "2025", title: "高清升级", text: "全面支持高清与 4K 画质播放，观看体验再上台阶。" },
  { year: "2026", title: "持续成长", text: "上线智能推荐与标签筛选，服务更多热爱视频的用户。" },
];

const stats = [
  { value: "10 万+", label: "在线视频资源" },
  { value: "8 大", label: "内容分类" },
  { value: "300 万", label: "月均播放次数" },
  { value: "4.8 分", label: "用户平均评分" },
];

export default function AboutPage() {
  useDocumentTitle(
    "关于我们 - 啪啪视频",
    "了解啪啪视频平台的定位、团队理念与发展愿景，以及我们如何为大众提供丰富的高清在线视频内容。",
    "关于啪啪视频,啪啪视频,视频分享平台,高清视频,在线视频平台",
  );

  return (
    <div className="w-full pb-16">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-play-circle-line"></i>
            关于啪啪视频
          </span>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            我们用视频，连接每一种好奇与热爱
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            啪啪视频是一个面向大众的高清在线视频分享平台。无论是想放松心情的娱乐短片，还是想认真学习的知识教程，我们都希望能在这里陪你度过。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=creative%20video%20team%20collaborating%20in%20a%20bright%20modern%20studio%20with%20cameras%20and%20screens%2C%20warm%20natural%20light%2C%20clean%20minimal%20aesthetic%2C%20ultra%20detailed%2C%20professional%20editorial%20photography&width=1000&height=750&seq=pp-about-story&orientation=landscape"
              alt="啪啪视频团队共同创作视频内容"
              title="啪啪视频 - 高清在线视频分享平台"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们的故事</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              2021 年，几个热爱影像的年轻人决定做一个「打开就能看好视频」的网站。没有复杂的操作，也不需要注册一堆账号，只要你愿意，随时都能找到想看的内容。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              几年间，啪啪视频从一个只有娱乐视频的小站，成长为涵盖生活、教育、科技、音乐、体育、美食与旅行的综合视频平台。我们始终相信：好内容值得被认真对待，也值得被更多人轻松发现。
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="font-heading text-xl font-bold text-primary-600">2021</p>
                <p className="mt-1 text-xs text-foreground-500">平台正式上线</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="font-heading text-xl font-bold text-accent-600">300 万</p>
                <p className="mt-1 text-xs text-foreground-500">月均播放次数</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">使命与愿景</h2>
            <p className="mt-2 text-sm text-foreground-500">让浏览视频变得简单，让分享内容变得自然。</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-compass-3-line text-2xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">我们的使命</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                用清晰细致的分类与贴心的推荐，帮助每一位用户在小屏幕与大屏幕之间，都能快速找到想看的高清视频内容。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-rocket-line text-2xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">我们的愿景</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                成为一个人人愿意停留、愿意分享的视频社区，让创作者与观众在这里彼此找到，让好内容持续生长。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6">
        <div className="mb-10 text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们坚持的价值观</h2>
          <p className="mt-2 text-sm text-foreground-500">这些原则，指引着平台的每一次选择。</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展历程</h2>
            <p className="mt-2 text-sm text-foreground-500">一路走来，感谢每一位用户的陪伴。</p>
          </div>
          <div className="grid gap-5 md:grid-cols-4">
            {milestones.map((item) => (
              <div key={item.year} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold text-accent-800">
                  <i className="ri-flag-2-line"></i>
                  {item.year}
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 pt-14 md:px-6">
        <div className="grid gap-4 rounded-lg bg-primary-950 p-8 sm:grid-cols-2 md:p-10 lg:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label} className="text-center">
              <p className="font-heading text-3xl font-bold text-background-50">{item.value}</p>
              <p className="mt-1.5 text-sm text-background-300">{item.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-5 rounded-lg border border-background-200 bg-background-50 p-8 text-center md:flex-row md:text-left">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">想和我们聊聊？</h2>
            <p className="mt-1.5 text-sm text-foreground-500">无论是内容建议还是合作意向，我们都期待听到你的声音。</p>
          </div>
          <Link
            to="/contact"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <i className="ri-mail-send-line mr-1.5"></i>
            联系我们
          </Link>
        </div>
      </section>
    </div>
  );
}